import { useState, useMemo, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Map as MapboxMap } from "mapbox-gl";
import { useRecordDeliveriesByAddresses, useUndoDeliveriesByAddresses } from "../../api/deliveries";
import { useBulkHideAddresses, useMergeAddresses } from "../../api/addresses/hooks";
import { getCachedFeatures } from "../layers/utils";
import { markAddressesAsDelivered, markAddressesAsUndelivered, updateAddressStatus, applyAddressMergeOnMap } from "../layers";
import type { AddressFeatureProperties } from "../../api/map/types";
import type { AddressResponse } from "../../api/addresses/types";
import type { DeliveryResponse } from "../../api/deliveries";
import { getCurrentSessionId } from "../utils/sessionManager";
import toast from "react-hot-toast";
import { AddressMapActionList, buildAddressGoogleMapsSearchUrl, canConvertAddressMapDeliveryToNoThanks, canMarkAddressMapDelivered, canMarkAddressMapNoThanks, canUndoAddressMapDelivery, canUndoAddressMapNoThanks } from "./AddressMapActionList";
import "./BulkAddressDeliveryPanel.css";

export interface BulkAddressDeliveryPanelProps {
    campaignId: string;
    deliveryActionsEnabled?: boolean;
    selectedAddressIds: string[];
    flyerTypeIds: string[];
    map?: MapboxMap | null;
    onClearSelection: () => void;
    // Props for single address operations (when selectedAddressIds.length === 1)
    singleAddress?: AddressResponse;
    onHide?: () => void;
    onBlock?: () => void;
    onMove?: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
    onUndoDelivery?: () => void;
    onMarkNoThanks?: () => void;
    onConvertToNoThanks?: () => void | Promise<void>;
    onUndoNoThanks?: () => void;
    onDeliveryHistory?: () => void;
    selectedFlyerTypeId?: string | null;
    deliveries?: DeliveryResponse[];
    currentUserId?: string;
}

export const BulkAddressDeliveryPanel = ({
    campaignId,
    deliveryActionsEnabled = true,
    selectedAddressIds,
    flyerTypeIds,
    map,
    onClearSelection,
    singleAddress,
    onHide,
    onBlock,
    onMove,
    onEdit,
    onCopy,
    onUndoDelivery,
    onMarkNoThanks,
    onConvertToNoThanks,
    onUndoNoThanks,
    onDeliveryHistory,
    selectedFlyerTypeId,
    deliveries = [],
    currentUserId
}: BulkAddressDeliveryPanelProps): JSX.Element | null => {
    const { t } = useTranslation();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState<"deliver" | "noThanks" | "hide" | "merge" | "undo" | null>(null);
    const [showAddressList, setShowAddressList] = useState(false);
    const [bottomOffset, setBottomOffset] = useState<string>("3.5rem");
    const panelRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const statusBar = document.querySelector(".map-status-bar");
        if (statusBar) {
            const height = statusBar.getBoundingClientRect().height;
            setBottomOffset(`${height + 16}px`);
        }
    }, []);

    const recordDeliveriesMutation = useRecordDeliveriesByAddresses();
    const undoDeliveriesMutation = useUndoDeliveriesByAddresses();
    const bulkHideMutation = useBulkHideAddresses();
    const mergeAddressesMutation = useMergeAddresses();

    // Get address information from map features
    const selectedAddresses = useMemo(() => {
        if (!map || selectedAddressIds.length === 0) {
            return [];
        }

        const allAddresses = getCachedFeatures<AddressFeatureProperties>(map, "addresses");
        const selectedSet = new Set(selectedAddressIds);

        return allAddresses
            .filter((feature) => {
                const id = feature.id ?? feature.properties.addressPointId;
                return id && selectedSet.has(String(id));
            })
            .map((feature) => {
                const props = feature.properties;
                const address = `${props.street}${props.number ? ` ${props.number}` : ""}${props.apartment ? ` ${props.apartment}` : ""}`;
                return {
                    id: props.addressPointId,
                    address,
                    city: props.city,
                    postalCode: props.postalCode
                };
            })
            .sort((a, b) => {
                // Sort by street name, then number
                if (a.address !== b.address) {
                    return a.address.localeCompare(b.address);
                }
                return a.city.localeCompare(b.city);
            });
    }, [map, selectedAddressIds]);

    // How many of the selected addresses actually have something to undo for the selected
    // flyer. Read off the map features — the panel only gets a delivery list for the
    // single-address case.
    const undoableAddressCount = useMemo(() => {
        if (!map || selectedAddressIds.length === 0 || flyerTypeIds.length === 0) {
            return 0;
        }
        const selectedSet = new Set(selectedAddressIds);
        const flyerSet = new Set(flyerTypeIds);
        return getCachedFeatures<AddressFeatureProperties>(map, "addresses").filter((f) => {
            if (!selectedSet.has(String(f.properties.addressPointId))) {
                return false;
            }
            return (f.properties.flyerStatuses ?? []).some(
                (fs) =>
                    ((fs.flyerTypeId && flyerSet.has(fs.flyerTypeId)) ||
                        (fs.campaignFlyerId && flyerSet.has(fs.campaignFlyerId))) &&
                    (fs.deliveredQuantity > 0 || !!fs.isNoThanks)
            );
        }).length;
    }, [map, selectedAddressIds, flyerTypeIds]);

    const estimatedMergeUnits = useMemo(() => {
        if (!map || selectedAddressIds.length < 2) {
            return 0;
        }
        const selectedSet = new Set(selectedAddressIds);
        return getCachedFeatures<AddressFeatureProperties>(map, "addresses")
            .filter((f) => selectedSet.has(String(f.properties.addressPointId)))
            .reduce((sum, f) => {
                const count = f.properties.deliveryUnitCount ?? 1;
                return sum + (count > 0 ? count : 1);
            }, 0);
    }, [map, selectedAddressIds]);

    if (selectedAddressIds.length === 0) {
        return null;
    }

    const handleMarkAsDelivered = () => {
        if (flyerTypeIds.length === 0) {
            return;
        }
        setConfirmAction("deliver");
        setShowConfirmDialog(true);
    };

    const handleMarkNoThanks = () => {
        if (flyerTypeIds.length === 0) {
            return;
        }
        setConfirmAction("noThanks");
        setShowConfirmDialog(true);
    };

    const handleHide = () => {
        setConfirmAction("hide");
        setShowConfirmDialog(true);
    };

    const handleUndoDeliveries = () => {
        if (flyerTypeIds.length === 0) {
            return;
        }
        setConfirmAction("undo");
        setShowConfirmDialog(true);
    };

    const handleMerge = () => {
        if (selectedAddressIds.length < 2) {
            return;
        }
        setConfirmAction("merge");
        setShowConfirmDialog(true);
    };

    const handleConfirmDeliver = () => {
        if (map) {
            markAddressesAsDelivered(
                map,
                selectedAddressIds,
                flyerTypeIds
            );
        }

        recordDeliveriesMutation.mutate(
            {
                campaignId,
                addressPointIds: selectedAddressIds,
                flyerTypeIds,
                clientRequestId: getCurrentSessionId()
            },
            {
                onSuccess: () => {
                    setShowConfirmDialog(false);
                    setConfirmAction(null);
                    onClearSelection();
                },
                onError: () => {
                    // Keep dialog open on error
                }
            }
        );
    };

    const handleConfirmNoThanks = () => {
        if (map) {
            markAddressesAsDelivered(
                map,
                selectedAddressIds,
                flyerTypeIds,
                undefined,
                undefined,
                { isNoThanks: true }
            );
        }

        recordDeliveriesMutation.mutate(
            {
                campaignId,
                addressPointIds: selectedAddressIds,
                flyerTypeIds,
                clientRequestId: getCurrentSessionId(),
                isNoThanks: true
            },
            {
                onSuccess: () => {
                    setShowConfirmDialog(false);
                    setConfirmAction(null);
                    onClearSelection();
                },
                onError: () => {
                    // Keep dialog open on error
                }
            }
        );
    };

    const handleConfirmUndo = () => {
        if (flyerTypeIds.length === 0) {
            return;
        }

        if (map) {
            markAddressesAsUndelivered(map, selectedAddressIds, flyerTypeIds, selectedFlyerTypeId ?? null);
        }

        undoDeliveriesMutation.mutate(
            {
                campaignId,
                addressPointIds: selectedAddressIds,
                flyerTypeIds
            },
            {
                onSuccess: (result) => {
                    toast.success(t("map.bulkAddress.undoSuccess", { count: result.deletedCount }));
                    setShowConfirmDialog(false);
                    setConfirmAction(null);
                    onClearSelection();
                },
                onError: (error) => {
                    // The hook's onError refetches, which repaints the optimistic change away.
                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                    toast.error(errorMessage);
                }
            }
        );
    };

    const handleConfirmHide = () => {
        if (map) {
            for (const id of selectedAddressIds) {
                updateAddressStatus(map, id, undefined, true);
            }
        }

        bulkHideMutation.mutate(
            {
                addressPointIds: selectedAddressIds,
                isHidden: true,
                ...(campaignId ? { campaignId } : {})
            },
            {
                onSuccess: () => {
                    toast.success(t("map.bulkAddress.hideSuccess", { count: selectedAddressIds.length }));
                    setShowConfirmDialog(false);
                    setConfirmAction(null);
                    onClearSelection();
                },
                onError: (error) => {
                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                    toast.error(errorMessage);
                }
            }
        );
    };

    const handleConfirmMerge = () => {
        mergeAddressesMutation.mutate(
            {
                addressPointIds: selectedAddressIds,
                ...(campaignId ? { campaignId } : {})
            },
            {
                onSuccess: (result) => {
                    if (map) {
                        applyAddressMergeOnMap(
                            map,
                            result.primaryAddress.id,
                            result.primaryAddress.latitude,
                            result.primaryAddress.longitude,
                            result.totalDeliveryUnitCount,
                            result.hiddenAddressPointIds,
                            selectedFlyerTypeId ?? undefined
                        );
                    }
                    toast.success(
                        t("map.bulkAddress.mergeSuccess", {
                            count: selectedAddressIds.length,
                            units: result.totalDeliveryUnitCount
                        })
                    );
                    setShowConfirmDialog(false);
                    setConfirmAction(null);
                    onClearSelection();
                },
                onError: (error) => {
                    const errorMessage = error instanceof Error ? error.message : t("map.addressContextMenu.error");
                    toast.error(errorMessage);
                }
            }
        );
    };

    const handleConfirm = () => {
        if (confirmAction === "deliver") {
            handleConfirmDeliver();
        } else if (confirmAction === "noThanks") {
            handleConfirmNoThanks();
        } else if (confirmAction === "hide") {
            handleConfirmHide();
        } else if (confirmAction === "merge") {
            handleConfirmMerge();
        } else if (confirmAction === "undo") {
            handleConfirmUndo();
        }
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
        setConfirmAction(null);
    };

    const isLoading =
        recordDeliveriesMutation.isPending ||
        undoDeliveriesMutation.isPending ||
        bulkHideMutation.isPending ||
        mergeAddressesMutation.isPending;

    const canUndo = canUndoAddressMapDelivery(selectedFlyerTypeId, onUndoDelivery, deliveries, currentUserId);
    const canUndoNoThanks = canUndoAddressMapNoThanks(selectedFlyerTypeId, onUndoNoThanks, deliveries, currentUserId);
    const showMarkDelivered =
        !!singleAddress && canMarkAddressMapDelivered(singleAddress, deliveries, selectedFlyerTypeId ?? null);
    const canShowMarkNoThanks =
        !!singleAddress &&
        !!selectedFlyerTypeId &&
        canMarkAddressMapNoThanks(selectedFlyerTypeId, singleAddress, deliveries);
    const canShowConvertToNoThanks =
        !!singleAddress &&
        canConvertAddressMapDeliveryToNoThanks(selectedFlyerTypeId ?? null, singleAddress, deliveries, currentUserId);

    const isSingleAddress = selectedAddressIds.length === 1;

    const dialogTitle =
        confirmAction === "hide"
            ? t("map.bulkAddress.confirmHideTitle")
            : confirmAction === "merge"
              ? t("map.bulkAddress.confirmMergeTitle")
              : confirmAction === "noThanks"
                ? t("map.bulkAddress.confirmNoThanksTitle")
              : confirmAction === "undo"
                ? t("map.bulkAddress.confirmUndoTitle")
              : t("map.bulkAddress.confirmTitle");

    const dialogMessage =
        confirmAction === "hide"
            ? t("map.bulkAddress.confirmHideMessage", { count: selectedAddressIds.length })
            : confirmAction === "merge"
              ? t("map.bulkAddress.confirmMergeMessage", {
                    count: selectedAddressIds.length,
                    units: estimatedMergeUnits
                })
              : confirmAction === "noThanks"
                ? t("map.bulkAddress.confirmNoThanksMessage", { count: selectedAddressIds.length })
              : confirmAction === "undo"
                ? t("map.bulkAddress.confirmUndoMessage", { count: undoableAddressCount })
              : t("map.bulkAddress.confirmMessage", { count: selectedAddressIds.length });

    return (
        <>
            <div ref={panelRef} className="bulk-address-panel" style={{ bottom: bottomOffset }}>
                <div className="bulk-address-panel__header">
                    {isSingleAddress && singleAddress ? (
                        <>
                            <div className="bulk-address-panel__address-info">
                                <h3 className="bulk-address-panel__title">
                                    <strong>{singleAddress.street}</strong>
                                    {singleAddress.number && <span> {singleAddress.number}</span>}
                                    {singleAddress.apartment && <span> {singleAddress.apartment}</span>}
                                </h3>
                                <div className="bulk-address-panel__address-details">
                                    {singleAddress.postalCode} {singleAddress.city}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="bulk-address-panel__clear-button"
                                onClick={onClearSelection}
                                disabled={isLoading}
                                aria-label={t("map.bulkAddress.clearSelection")}
                            >
                                ✕
                            </button>
                        </>
                    ) : (
                        <>
                            <h3 className="bulk-address-panel__title">
                                {t("map.bulkAddress.selected", { count: selectedAddressIds.length })}
                            </h3>
                            <button
                                type="button"
                                className="bulk-address-panel__clear-button"
                                onClick={onClearSelection}
                                disabled={isLoading}
                                aria-label={t("map.bulkAddress.clearSelection")}
                            >
                                ✕
                            </button>
                        </>
                    )}
                </div>
                {!isSingleAddress && (
                    <div className="bulk-address-panel__summary">
                        <button
                            type="button"
                            className="bulk-address-panel__toggle-list"
                            onClick={() => setShowAddressList(!showAddressList)}
                            aria-expanded={showAddressList}
                        >
                            {showAddressList ? t("map.bulkAddress.hideList") : t("map.bulkAddress.showList")}
                            <span className="bulk-address-panel__toggle-icon">
                                {showAddressList ? "▼" : "▶"}
                            </span>
                        </button>
                    </div>
                )}

                {!isSingleAddress && showAddressList && selectedAddresses.length > 0 && (
                    <div className="bulk-address-panel__address-list">
                        <ul className="bulk-address-panel__address-items">
                            {selectedAddresses.map((addr) => (
                                <li key={addr.id} className="bulk-address-panel__address-item">
                                    <span className="bulk-address-panel__address-text">
                                        {addr.address}
                                    </span>
                                    <span className="bulk-address-panel__address-meta">
                                        {addr.postalCode} {addr.city}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Context menu actions for single address */}
                {isSingleAddress && singleAddress && (
                    <div className="bulk-address-panel__context-actions">
                        <div className="bulk-address-panel__context-divider" />
                        <div className="bulk-address-panel__context-buttons">
                            <AddressMapActionList
                                variant="panel"
                                address={singleAddress}
                                googleMapsUrl={buildAddressGoogleMapsSearchUrl(singleAddress)}
                                isLoading={isLoading}
                                markDeliveredDisabled={flyerTypeIds.length === 0}
                                canUndo={Boolean(canUndo)}
                                canUndoNoThanks={canUndoNoThanks}
                                showMarkDelivered={showMarkDelivered}
                                showMarkNoThanks={deliveryActionsEnabled && canShowMarkNoThanks}
                                showConvertToNoThanks={deliveryActionsEnabled && canShowConvertToNoThanks}
                                dismissAfterAction={onClearSelection}
                                keepHostOpenOnDeliveryHistory
                                dismissAfterMarkDelivered={false}
                                onDeliveryHistory={onDeliveryHistory}
                                onMarkDelivered={deliveryActionsEnabled && showMarkDelivered ? handleMarkAsDelivered : undefined}
                                onMarkNoThanks={
                                    deliveryActionsEnabled && canShowMarkNoThanks
                                        ? onMarkNoThanks ?? handleMarkNoThanks
                                        : undefined
                                }
                                onConvertToNoThanks={
                                    deliveryActionsEnabled && canShowConvertToNoThanks ? onConvertToNoThanks : undefined
                                }
                                onUndoDelivery={deliveryActionsEnabled && canUndo ? onUndoDelivery : undefined}
                                onUndoNoThanks={deliveryActionsEnabled && canUndoNoThanks ? onUndoNoThanks : undefined}
                                onHide={onHide}
                                onBlock={onBlock}
                                onMove={onMove}
                                onEdit={onEdit}
                                onCopy={onCopy}
                            />
                        </div>
                    </div>
                )}

                {/* Bulk delivery / hide / merge — only when a specific campaign is selected */}
                {!isSingleAddress && deliveryActionsEnabled && (
                    <div className="bulk-address-panel__actions bulk-address-panel__actions--multi">
                        <button
                            type="button"
                            className="bulk-address-panel__button bulk-address-panel__button--primary"
                            onClick={handleMarkAsDelivered}
                            disabled={isLoading || flyerTypeIds.length === 0}
                        >
                            {isLoading && confirmAction === "deliver"
                                ? t("common.loading")
                                : t("map.bulkAddress.markAsDelivered")}
                        </button>
                        <button
                            type="button"
                            className="bulk-address-panel__button bulk-address-panel__button--secondary"
                            onClick={handleMarkNoThanks}
                            disabled={isLoading || flyerTypeIds.length === 0}
                        >
                            {isLoading && confirmAction === "noThanks"
                                ? t("common.loading")
                                : t("map.addressContextMenu.markNoThanks")}
                        </button>
                        <button
                            type="button"
                            className="bulk-address-panel__button bulk-address-panel__button--secondary"
                            onClick={handleUndoDeliveries}
                            disabled={isLoading || flyerTypeIds.length === 0 || undoableAddressCount === 0}
                            title={t("map.bulkAddress.undoHint")}
                        >
                            {isLoading && confirmAction === "undo"
                                ? t("common.loading")
                                : t("map.addressContextMenu.undoDelivery")}
                        </button>
                        <button
                            type="button"
                            className="bulk-address-panel__button bulk-address-panel__button--secondary"
                            onClick={handleHide}
                            disabled={isLoading}
                        >
                            {isLoading && confirmAction === "hide"
                                ? t("common.loading")
                                : t("map.bulkAddress.hideAddresses")}
                        </button>
                        <button
                            type="button"
                            className="bulk-address-panel__button bulk-address-panel__button--secondary"
                            onClick={handleMerge}
                            disabled={isLoading || selectedAddressIds.length < 2}
                            title={t("map.bulkAddress.mergeToPointHint")}
                        >
                            {isLoading && confirmAction === "merge"
                                ? t("common.loading")
                                : t("map.bulkAddress.mergeToPoint")}
                        </button>
                    </div>
                )}
            </div>

            {showConfirmDialog && (
                <div className="bulk-address-panel__overlay" onClick={handleCancel}>
                    <div
                        className="bulk-address-panel__dialog"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-labelledby="bulk-address-dialog__title"
                        aria-modal="true"
                    >
                        <header className="bulk-address-panel__dialog-header">
                            <h2 id="bulk-address-dialog__title" className="bulk-address-panel__dialog-title">
                                {dialogTitle}
                            </h2>
                        </header>
                        <div className="bulk-address-panel__dialog-body">
                            <p>{dialogMessage}</p>
                            {selectedAddresses.length > 0 && selectedAddresses.length <= 20 && (
                                <div className="bulk-address-panel__dialog-address-list">
                                    <strong>{t("map.bulkAddress.selectedAddresses")}:</strong>
                                    <ul className="bulk-address-panel__dialog-address-items">
                                        {selectedAddresses.map((addr) => (
                                            <li key={addr.id}>
                                                {addr.address}, {addr.postalCode} {addr.city}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {selectedAddresses.length > 20 && (
                                <p className="bulk-address-panel__dialog-note">
                                    {t("map.bulkAddress.tooManyToShow", { count: selectedAddresses.length })}
                                </p>
                            )}
                        </div>
                        <footer className="bulk-address-panel__dialog-footer">
                            <button
                                type="button"
                                className="bulk-address-panel__button bulk-address-panel__button--cancel"
                                onClick={handleCancel}
                                disabled={isLoading}
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                type="button"
                                className="bulk-address-panel__button bulk-address-panel__button--confirm"
                                onClick={handleConfirm}
                                disabled={isLoading}
                            >
                                {isLoading ? t("common.loading") : t("common.confirm")}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </>
    );
};